import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateIt } from "@/lib/services";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CommunicationDetailDialog } from "@/components/worker/CommunicationDetail";
import { AlertTriangle, Users2, ClipboardList, FileText, ChevronRight, CalendarClock, CalendarDays, BellRing, Megaphone } from "lucide-react";

const CAT = {
  presenze: { icon: Users2, label: "Presenze" },
  relazioni: { icon: ClipboardList, label: "Relazioni" },
  documentazione: { icon: FileText, label: "Documentazione" },
  agenda: { icon: CalendarDays, label: "Agenda" },
  solleciti: { icon: BellRing, label: "Sollecito" },
  comunicazioni: { icon: Megaphone, label: "Comunicazione" },
};

function AlertRow({ it, onCta, onOpen, compact }) {
  const cat = CAT[it.category] || { icon: AlertTriangle, label: "Avviso" };
  const Icon = cat.icon;
  const info = it.severity === "info";
  const critical = it.severity === "critical";
  const box = info ? "border-sky-200 bg-sky-50/70" : critical ? "border-red-200 bg-red-50/70" : "border-amber-200 bg-amber-50/60";
  const badge = info ? "bg-sky-100 text-sky-700" : critical ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";
  const clickable = it.category === "comunicazioni" && !!it.comm_id && !!onOpen;
  return (
    <div data-testid={`alert-item-${it.category}`}
      onClick={clickable ? () => onOpen(it) : undefined}
      role={clickable ? "button" : undefined}
      className={`rounded-xl border px-3 py-2.5 ${box} ${clickable ? "cursor-pointer hover:brightness-[0.985] active:scale-[0.995] transition" : ""}`}>
      <div className="flex items-start gap-2.5">
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${badge}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{cat.label}</p>
          <p className="text-sm font-semibold text-slate-800 leading-tight">{it.title}</p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {it.date && <span className="capitalize">{fmtDateIt(it.date)}</span>}{it.date && it.context ? " · " : ""}{it.context}
          </p>
          {it.body && (
            <p className={`text-sm text-slate-700 mt-1 whitespace-pre-wrap ${compact ? "line-clamp-2" : ""}`} data-testid="alert-body">{it.body}</p>
          )}
          {clickable && (
            <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sky-700">Apri e conferma <ChevronRight className="h-3.5 w-3.5" /></span>
          )}
          {it.deadline && (
            <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
              <CalendarClock className="h-3 w-3" /> Entro il {fmtDateIt(it.deadline)}
            </p>
          )}
          {!compact && it.cta && (
            <button data-testid={`alert-cta-${it.category}`} onClick={() => onCta(it)}
              className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 transition-colors ${critical ? "bg-red-600 text-white hover:bg-red-700" : "bg-[#22364D] text-white hover:bg-[#2f4a6b]"}`}>
              {it.cta.label} <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ImportantAlerts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = () => api.get("/my/important-alerts").then((r) => setData(r.data)).catch(() => setData({ count: 0, items: [] }));
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("orbit:comm-archived", h);
    return () => window.removeEventListener("orbit:comm-archived", h);
  }, []);

  if (!data || data.count === 0) return null;
  const items = data.items || [];
  const preview = items.slice(0, 3);
  const extra = items.length - preview.length;
  const onCta = (it) => { setOpen(false); if (it.cta?.link) navigate(it.cta.link); };
  const onOpen = (it) => setDetail(it);
  const confirmRead = async () => {
    if (!detail?.comm_id) return;
    setConfirming(true);
    try {
      await api.post(`/my/communications/${detail.comm_id}/read`);
      toast.success("Comunicazione archiviata");
      setDetail(null);
      await load();
      window.dispatchEvent(new CustomEvent("orbit:comm-archived"));
    } catch (e) { toast.error("Impossibile confermare la lettura"); }
    finally { setConfirming(false); }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        data-testid="important-alerts-card"
        className="mt-4 rounded-2xl border border-amber-300 bg-white shadow-[0_2px_10px_rgba(180,120,0,0.08)] overflow-hidden">
        <button onClick={() => setOpen(true)} data-testid="important-alerts-open"
          className="w-full flex items-center gap-2.5 px-4 py-3 border-b border-amber-100 bg-amber-50/70 text-left hover:bg-amber-50 transition-colors">
          <span className="h-9 w-9 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="font-heading font-bold text-slate-900 flex items-center gap-2">
              Avvisi importanti
              <span data-testid="important-alerts-badge"
                className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-xs font-bold inline-flex items-center justify-center">
                {data.count}
              </span>
            </span>
            <span className="block text-xs text-slate-500">Azioni che richiedono il tuo intervento</span>
          </span>
          <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
        </button>
        <div className="p-3 space-y-2">
          {preview.map((it, i) => <AlertRow key={i} it={it} onCta={onCta} onOpen={onOpen} compact />)}
          <button onClick={() => setOpen(true)} data-testid="important-alerts-seeall"
            className="w-full text-center text-xs font-semibold text-primary hover:underline py-1.5">
            {extra > 0 ? `+ ${extra} altri avvisi · Vedi tutti` : "Vedi tutti"}
          </button>
        </div>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="important-alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Avvisi importanti
              <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-xs font-bold inline-flex items-center justify-center">{data.count}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {items.map((it, i) => <AlertRow key={i} it={it} onCta={onCta} onOpen={onOpen} />)}
          </div>
        </DialogContent>
      </Dialog>

      <CommunicationDetailDialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}
        comm={detail} onConfirm={confirmRead} confirming={confirming} />
    </>
  );
}

export default ImportantAlerts;
